import ReactHtmlParser, {convertNodeToReactElement} from "@hedgedoc/html-to-react";
import {Typography} from "@mui/material";
import dompurify from "dompurify";
import linkify from "linkify-html";
import {memo} from "react";

import Flex from "./Flex";
import Link from "./Link";

import scss from "./Html.module.scss";

/**
 * @param {?string} content
 * @param {boolean} boldLinks make anchor tags bold
 */
const Html = memo(({content, boldLinks}) => {

	/**
	 * Transform an html-to-react node.
	 *
	 * @param {Node} node
	 * @param {Integer} nodeIndex
	 * @return {ReactNode}
	 */
	const transformNode = (node, nodeIndex) => {
		if ((node.name === "p") || ((node.type === "text") && (node.parent.type === "root"))) {
			return (
				<Typography
					component={((node.name === "p") ? "div" : undefined)}
					key={nodeIndex}>
					{nodeToReact(node)}
				</Typography>
			);
		}
		else if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(node.name)) {

			const variantMap = {
				h1: "h5",
				h2: "h6",
				h3: "h6",
				h4: "h6",
				h5: "h6",
				h6: "h6"
			};

			return (
				<Typography
					component="div"
					key={nodeIndex}
					variant={variantMap[node.name]}>
					{nodeToReact(node)}
				</Typography>
			);

		}
		else if (node.name === "a") {
			return (
				<Link key={nodeIndex} uri={node.attribs.href} rel="noreferrer" target="_blank">{nodeChildrenToReact(node)}</Link>
			);
		}
		else if ((node.name === "img") && node.attribs.alt) {
			return (
				<Flex gap={0} key={nodeIndex}>
					{convertNodeToReactElement(node)}
					<figcaption>
						<i>{node.attribs.alt}</i>
					</figcaption>
				</Flex>
			);
		}
		else return undefined;
	};

	/**
	 * Transform HTML text to a React node.
	 *
	 * @param {String} html
	 * @return {ReactNode}
	 */
	const htmlToReact = html => {
		return ReactHtmlParser(html, {transform: transformNode});
	};

	/**
	 * Transform a DOM node (from html-to-react) to a React node.
	 *
	 * @param {Node} node
	 * @param {Integer} nodeIndex
	 * @return {ReactNode}
	 */
	const nodeToReact = (node, nodeIndex) => {
		return convertNodeToReactElement(node, nodeIndex, transformNode);
	};

	/**
	 * Transform a DOM node's childen (from html-to-react) to React nodes.
	 *
	 * @param {Node} node
	 * @return {Array<ReactNode>}
	 */
	const nodeChildrenToReact = node => {
		return node.children.map((child, i) => {
			return nodeToReact(child, i);
		});
	};

	/**
	 * Render!
	 */
	return (
		<div className={`${scss.root} ${boldLinks ? scss.boldLinks : ""} MuiTypography-body1`}>
			{
				htmlToReact(
					dompurify.sanitize(
						linkify(
							content,
							{
								target: "_blank",
								defaultProtocol: "https"
							}
						),
						{
							ADD_ATTR: ["target"]
						}
					)
				)
			}
		</div>
	);

});

export default Html;
